import * as THREE from 'three';

/** Deterministic PRNG so a given surface looks the same every run. */
function rng(seed) {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

function makeCanvas(size) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  return c;
}

/** Every generator reads pixels back, so the hint is always worth setting. */
function ctx2d(canvas) {
  return canvas.getContext('2d', { willReadFrequently: true });
}

function toTexture(canvas, { srgb = false, repeat = 1, aniso = 4 } = {}) {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = aniso;
  t.needsUpdate = true;
  return t;
}

/**
 * Sobel a greyscale height canvas into a tangent space normal map.
 * Cheaper and far smaller than shipping authored normal maps, and it keeps
 * every surface consistent with the albedo that generated it.
 */
function heightToNormal(heightCanvas, strength = 2.2) {
  const size = heightCanvas.width;
  const src = ctx2d(heightCanvas).getImageData(0, 0, size, size).data;
  const out = makeCanvas(size);
  const ctx = ctx2d(out);
  const img = ctx.createImageData(size, size);
  const at = (x, y) => {
    const xx = (x + size) % size;
    const yy = (y + size) % size;
    return src[(yy * size + xx) * 4] / 255;
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const tl = at(x - 1, y - 1), t = at(x, y - 1), tr = at(x + 1, y - 1);
      const l = at(x - 1, y), r = at(x + 1, y);
      const bl = at(x - 1, y + 1), b = at(x, y + 1), br = at(x + 1, y + 1);
      const dx = (tr + 2 * r + br) - (tl + 2 * l + bl);
      const dy = (bl + 2 * b + br) - (tl + 2 * t + tr);
      let nx = -dx * strength;
      let ny = -dy * strength;
      const nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len; ny /= len;
      const i = (y * size + x) * 4;
      img.data[i] = (nx * 0.5 + 0.5) * 255;
      img.data[i + 1] = (ny * 0.5 + 0.5) * 255;
      img.data[i + 2] = ((nz / len) * 0.5 + 0.5) * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return out;
}

function grain(ctx, size, amount, seed) {
  const rand = rng(seed);
  const img = ctx.getImageData(0, 0, size, size);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (rand() - 0.5) * amount;
    d[i] = Math.max(0, Math.min(255, d[i] + n));
    d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + n));
    d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + n));
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * The signature surface of the game: a tiled ceramic panel with recessed
 * seams, corner bolts and a wear pass. `accent` lights the seams so the player
 * can read portal-ready geometry from across a chasm.
 */
export function panelSet({
  size = 512,
  cells = 4,
  base = '#20293c',
  base2 = '#182131',
  seam = '#080c14',
  accent = null,
  wear = 0.5,
  seed = 7,
} = {}) {
  const rand = rng(seed);
  const cell = size / cells;

  // ---- albedo
  const albedo = makeCanvas(size);
  const a = ctx2d(albedo);
  a.fillStyle = base;
  a.fillRect(0, 0, size, size);

  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      const shade = 0.95 + rand() * 0.1;
      a.globalAlpha = 1;
      a.fillStyle = rand() > 0.72 ? base2 : base;
      a.fillRect(x * cell, y * cell, cell, cell);
      a.globalAlpha = Math.abs(shade - 1) * 0.9;
      a.fillStyle = shade > 1 ? '#ffffff' : '#000000';
      a.fillRect(x * cell, y * cell, cell, cell);
      a.globalAlpha = 1;

      // brushed streaks
      const streaks = 5 + Math.floor(rand() * 6);
      for (let s = 0; s < streaks; s++) {
        a.globalAlpha = 0.04 + rand() * 0.06;
        a.fillStyle = rand() > 0.5 ? '#ffffff' : '#000000';
        const sy = y * cell + rand() * cell;
        a.fillRect(x * cell + 2, sy, cell - 4, 1 + rand() * 2);
      }
      a.globalAlpha = 1;
    }
  }

  // seams
  a.strokeStyle = seam;
  a.lineWidth = Math.max(2, size / 190);
  for (let i = 0; i <= cells; i++) {
    a.beginPath(); a.moveTo(i * cell, 0); a.lineTo(i * cell, size); a.stroke();
    a.beginPath(); a.moveTo(0, i * cell); a.lineTo(size, i * cell); a.stroke();
  }

  // bolts
  a.fillStyle = '#0d1420';
  const boltR = Math.max(2, size / 150);
  for (let y = 0; y <= cells; y++) {
    for (let x = 0; x <= cells; x++) {
      a.beginPath(); a.arc(x * cell, y * cell, boltR, 0, Math.PI * 2); a.fill();
    }
  }

  // wear scuffs
  a.globalAlpha = 0.06 * wear;
  for (let i = 0; i < 90 * wear; i++) {
    a.fillStyle = rand() > 0.6 ? '#7f96b5' : '#04070d';
    const w = 4 + rand() * 40;
    a.fillRect(rand() * size, rand() * size, w, 1 + rand() * 3);
  }
  a.globalAlpha = 1;
  grain(a, size, 14, seed + 11);

  // ---- height (for the normal map)
  const height = makeCanvas(size);
  const h = ctx2d(height);
  h.fillStyle = '#b4b4b4';
  h.fillRect(0, 0, size, size);
  h.strokeStyle = '#1a1a1a';
  h.lineWidth = Math.max(3, size / 150);
  for (let i = 0; i <= cells; i++) {
    h.beginPath(); h.moveTo(i * cell, 0); h.lineTo(i * cell, size); h.stroke();
    h.beginPath(); h.moveTo(0, i * cell); h.lineTo(size, i * cell); h.stroke();
  }
  h.fillStyle = '#f2f2f2';
  for (let y = 0; y <= cells; y++) {
    for (let x = 0; x <= cells; x++) {
      h.beginPath(); h.arc(x * cell, y * cell, boltR * 1.15, 0, Math.PI * 2); h.fill();
    }
  }
  grain(h, size, 26, seed + 3);

  // ---- roughness: seams and bolts polish differently to the panel face
  const rough = makeCanvas(size);
  const r = ctx2d(rough);
  r.fillStyle = '#9a9a9a';
  r.fillRect(0, 0, size, size);
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      const v = Math.floor(150 + rand() * 45);
      r.fillStyle = `rgb(${v},${v},${v})`;
      r.fillRect(x * cell + 3, y * cell + 3, cell - 6, cell - 6);
    }
  }
  r.strokeStyle = '#4a4a4a';
  r.lineWidth = Math.max(3, size / 150);
  for (let i = 0; i <= cells; i++) {
    r.beginPath(); r.moveTo(i * cell, 0); r.lineTo(i * cell, size); r.stroke();
    r.beginPath(); r.moveTo(0, i * cell); r.lineTo(size, i * cell); r.stroke();
  }
  grain(r, size, 34, seed + 5);

  const result = {
    map: toTexture(albedo, { srgb: true }),
    normalMap: toTexture(heightToNormal(height, 2.0)),
    roughnessMap: toTexture(rough),
  };

  // ---- emissive seam glow marks a surface as portal ready
  if (accent) {
    const em = makeCanvas(size);
    const e = ctx2d(em);
    e.fillStyle = '#000000';
    e.fillRect(0, 0, size, size);
    e.strokeStyle = accent;
    e.lineWidth = Math.max(1, size / 340);
    e.shadowColor = accent;
    // Keep the halo tight. A wide blur here turns every wall into a light
    // source once bloom gets hold of it.
    e.shadowBlur = size / 110;
    for (let i = 0; i <= cells; i++) {
      e.beginPath(); e.moveTo(i * cell, 0); e.lineTo(i * cell, size); e.stroke();
      e.beginPath(); e.moveTo(0, i * cell); e.lineTo(size, i * cell); e.stroke();
    }
    e.shadowBlur = 0;
    // a few live circuit runs
    const runs = 3;
    e.lineWidth = Math.max(1, size / 420);
    for (let i = 0; i < runs; i++) {
      e.beginPath();
      let x = Math.floor(rand() * cells) * cell;
      let y = Math.floor(rand() * cells) * cell;
      e.moveTo(x, y);
      for (let s = 0; s < 4; s++) {
        if (rand() > 0.5) x += (rand() > 0.5 ? 1 : -1) * cell;
        else y += (rand() > 0.5 ? 1 : -1) * cell;
        e.lineTo(x, y);
      }
      e.stroke();
    }
    result.emissiveMap = toTexture(em, { srgb: true });
  }

  return result;
}

/** Industrial grating: dark bars with deep gaps. Never portal friendly. */
export function grateSet({ size = 512, bars = 16, seed = 21 } = {}) {
  const albedo = makeCanvas(size);
  const a = ctx2d(albedo);
  a.fillStyle = '#070a11';
  a.fillRect(0, 0, size, size);
  const step = size / bars;
  a.fillStyle = '#2b3444';
  for (let i = 0; i < bars; i++) {
    a.fillRect(i * step, 0, step * 0.42, size);
    a.fillRect(0, i * step, size, step * 0.42);
  }
  a.fillStyle = 'rgba(255,255,255,0.05)';
  for (let i = 0; i < bars; i++) a.fillRect(i * step, 0, step * 0.1, size);
  grain(a, size, 18, seed);

  const height = makeCanvas(size);
  const h = ctx2d(height);
  h.fillStyle = '#101010';
  h.fillRect(0, 0, size, size);
  h.fillStyle = '#e6e6e6';
  for (let i = 0; i < bars; i++) {
    h.fillRect(i * step, 0, step * 0.42, size);
    h.fillRect(0, i * step, size, step * 0.42);
  }

  const rough = makeCanvas(size);
  const r = ctx2d(rough);
  r.fillStyle = '#5c5c5c';
  r.fillRect(0, 0, size, size);
  grain(r, size, 60, seed + 2);

  return {
    map: toTexture(albedo, { srgb: true }),
    normalMap: toTexture(heightToNormal(height, 2.8)),
    roughnessMap: toTexture(rough),
  };
}

/** Diagonal hazard stripes for kill volumes and no-portal trim. */
export function hazardTexture({ size = 256, a = '#12060c', b = '#ff2fd0', bands = 8 } = {}) {
  const c = makeCanvas(size);
  const ctx = ctx2d(c);
  ctx.fillStyle = a;
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = b;
  ctx.lineWidth = size / (bands * 2.2);
  ctx.beginPath();
  for (let i = -size; i < size * 2; i += size / bands) {
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size, size);
  }
  ctx.stroke();
  return toTexture(c, { srgb: true });
}

/** Soft radial sprite used for sparks, dust and glow billboards. */
export function glowSprite(size = 128, color = '#ffffff') {
  const c = makeCanvas(size);
  const ctx = ctx2d(c);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, color);
  g.addColorStop(0.25, 'rgba(255,255,255,0.45)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Blue noise-ish tile for the film grain pass. */
export function noiseTexture(size = 256) {
  const c = makeCanvas(size);
  const ctx = ctx2d(c);
  const img = ctx.createImageData(size, size);
  const rand = rng(1337);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = rand() * 255;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return toTexture(c);
}
